/**
 * Centralized color management system for map features
 */

// Predefined color schemes
export const COLOR_SCHEMES = {
  // Default color scheme
  default: {
    primary: '#088',
    secondary: '#f0f0f0',
    accent: '#ff6b35',
    success: '#28a745',
    warning: '#ffc107',
    danger: '#dc3545',
    info: '#17a2b8'
  },
  
  // Nature-inspired colors
  nature: {
    primary: '#2d5016',
    secondary: '#8fbc8f',
    accent: '#d2691e',
    success: '#228b22',
    warning: '#daa520',
    danger: '#b22222',
    info: '#4682b4'
  },
  
  // Hunting-specific colors
  hunting: {
    primary: '#8b4513',
    secondary: '#f4a460',
    accent: '#ff4500',
    success: '#32cd32',
    warning: '#ff8c00',
    danger: '#b22222',
    info: '#4169e1'
  }
};

// Feature type colors
export const FEATURE_COLORS = {
  stand: '#8b4513',
  food_plot: '#228b22',
  water_source: '#4169e1',
  trail: '#696969',
  boundary: '#ff8c00', // legacy default; runtime will prefer CSS var
  hunt_location: '#32cd32',
  personal_feature: '#088',
  club_feature: '#8b4513',
  polygon: '#ff8c00' // legacy default; runtime will prefer CSS var
};

// Sign-out status colors
export const SIGN_OUT_COLORS = {
  available: '#28a745',    // Green for available locations
  occupied: '#dc3545',     // Red for occupied locations
  pending: '#ffc107'       // Yellow for pending sign-outs
};

/**
 * Get color for a feature type
 * @param {string} featureType - Type of feature
 * @param {string} scheme - Color scheme to use
 * @returns {string} Color hex code
 */
export function getFeatureColor(featureType, scheme = 'default') {
  const colors = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.default;
  // Resolve CSS variable when available (design tokens)
  const getCssVar = (name, fallback) => {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
      return v || fallback;
    } catch (_) {
      return fallback;
    }
  };
  const cream = getCssVar('--color-cream', '#f6f5f2');
  
  switch (featureType) {
    case 'stand':
      return colors.primary;
    case 'food_plot':
      return colors.success;
    case 'water_source':
      return colors.info;
    case 'trail':
      return colors.secondary;
    case 'boundary':
    case 'polygon':
      return cream; // Use token-driven cream by default
    case 'hunt_location':
      return colors.success;
    default:
      return colors.primary;
  }
}

/**
 * Get color for sign-out status
 * @param {string} status - 'available', 'occupied', or 'pending'
 * @returns {string} Color hex code
 */
export function getSignOutColor(status) {
  return SIGN_OUT_COLORS[status] || SIGN_OUT_COLORS.available;
}

/**
 * Get color with opacity
 * @param {string} color - Base color
 * @param {number} opacity - Opacity (0-1)
 * @returns {string} Color with opacity
 */
export function getColorWithOpacity(color, opacity = 1) {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Generate a color palette for multiple features
 * @param {number} count - Number of colors needed
 * @param {string} scheme - Color scheme to use
 * @returns {Array} Array of color hex codes
 */
export function generateColorPalette(count, scheme = 'default') {
  const colors = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.default;
  const baseColors = Object.values(colors);
  
  const palette = [];
  for (let i = 0; i < count; i++) {
    palette.push(baseColors[i % baseColors.length]);
  }
  
  return palette;
}
